import { getInstance } from '@/main';
import { ToastPositions, useToast } from 'vue-toast-notification';
import html2pdf from 'html2pdf.js';
import { useUserStore } from '@/stores/user';
import axios from 'axios';
export default {
  download(url, filename = '') {
    const a = document.createElement('a');
    a.style.display = 'none';
    a.href = url;
    a.download = filename;
    a.target = '_BLANK';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  },
  displayAllErrorsAsToasts(error) {
    const toast = useToast({
      position: ToastPositions.TOP_RIGHT,
      timeout: 3000,
    });

    if (error.response?.status === 500) {
      toast.error('Internal server error.');
      return;
    }

    if (error.response?.data?.error) {
      toast.error(error.response.data.error);
      return;
    }

    if (error.response?.data?.errors) {
      for (const value of Object.values(error.response.data.errors)) toast.error(value.join('<br>'));
      return;
    }

    if (Object.entries(error.response?.data?.errors || {}).length === 0 && error.response?.data?.message) {
      toast.error(error.response?.data?.message);
      return;
    }

    if (typeof error.response?.data === 'string') {
      toast.error(error.response.data);
      return;
    }

    if (error.response && getInstance()) {
      if (error.response.status === 401) {
        toast.error('You are not authorized to perform this action.');
      } else if (error.response.status === 403) {
        toast.error('You are not authorized to perform this action.');
      } else if (error.response.status === 404) {
        toast.error('The requested resource was not found.');
      } else if (error.response.status === 500) {
        toast.error('Internal server error.');
      } else if (error.response.status === 422) {
        toast.error('The request was well-formed but was unable to be followed due to semantic errors.');
      } else if (error.response.status === 400) {
        toast.error('The request was not well-formed.');
      } else if (error.response.status === 429) {
        toast.error('Too many requests.');
      } else if (error.response.status === 503) {
        toast.error('Service unavailable.');
      } else if (error.response.status === 504) {
        toast.error('Gateway timeout.');
      } else {
        toast.error('Something went wrong.');
      }
    }
  },
  successToast(message) {
    const toast = useToast({
      position: ToastPositions.TOP_RIGHT,
      timeout: 3000,
    });
    toast.success(message);
  },
  errorToast(message) {
    const toast = useToast({
      position: ToastPositions.TOP_RIGHT,
      timeout: 3000,
    });
    toast.error(message);
  },
  dateFormat(date, forceShowSeconds = false) {
    if (typeof date === 'string') date = new Date(date);

    const options = {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    };

    if (date.getSeconds() !== 0 || forceShowSeconds) options.second = '2-digit';

    return date.toLocaleString('hu-HU', options);
  },
  getDataCompleteness(object) {
    let fieldsOccupied = 0;

    const keys = Object.keys(object);
    keys.forEach((key, index) => {
      if (object[key] !== null) {
        fieldsOccupied++;
      }
    });
    return `${fieldsOccupied}%`;
  },
  uniqid(prefix = '', random = false) {
    const sec = Date.now() * 1000 + Math.random() * 1000;
    const id = sec.toString(16).replace(/\./g, '').padEnd(14, '0');
    return `${prefix}${id}${random ? `.${Math.trunc(Math.random() * 100000000)}` : ''}`;
  },
  scrollTo(h) {
    var url = location.href;
    location.href = '#' + h;
    history.replaceState(null, null, url);
  },
  /**
   * @param {string | Date} date
   * @param {"hu-HU"} locale
   * @return {string}
   */
  formatDate(date, locale = 'hu-HU') {
    const formatter = new Intl.DateTimeFormat(locale);
    if (typeof date === 'string') {
      // change the european date format to american
      const [day, month, year] = date.split('.');
      date = `${month}.${day}.${year}`;
      date = new Date(date);
    }
    return formatter.format(date);
  },
  isNumber(value) {
    return !isNaN(Number(value));
  },
  /**
   * @param {string} value
   * @return {boolean}
   */
  isEmail(value) {
    const re =
      /^(([^<>()[\].,;:\s@"]+(\.[^<>()[\].,;:\s@"]+)*)|(".+"))@(([^<>()[\].,;:\s@"]+\.)+[^<>()[\].,;:\s@"]{2,})$/i;
    return re.test(value.toLowerCase());
  },
  /**
   * @param {string} value
   * @return {boolean}
   */
  isPhoneNumber(value) {
    const re = /^(\+36|06)(20|30|70|31|25|1|2|3|4|5|6|7|8|9)\d{7,8}$/;
    return re.test(value);
  },
  /**
   * @param {string} template
   * @return {string}
   */
  parseEmailTemplate(template) {
    return template
      .replace(/A\(z\)\s*(?=[AÁEÉIÍOÓÖŐUÚÜŰaáeéiíoóöőuúüű])/g, 'Az ')
      .replace(/a\(z\)\s*(?=[AÁEÉIÍOÓÖŐUÚÜŰ])/gi, 'az ')
      .replace(/A\(z\)/g, 'A')
      .replace(/a\(z\)/gi, 'a');
  },
  /**
   * Replace `{varName}` placeholders for names listed in `variables` using `values` (keys = variable names).
   * @param {string} html
   * @param {string[]|undefined} variables
   * @param {Record<string, string>} values
   * @return {string}
   */
  replaceStatementVariables(html, variables, values) {
    if (html == null || html === '') return '';
    let out = String(html);
    const names = Array.isArray(variables) ? variables : [];
    for (const name of names) {
      const val = values[name] != null ? String(values[name]) : '';
      out = out.split(`{${name}}`).join(val);
    }
    return this.parseEmailTemplate(out);
  },
  /**
   * @param {'insider_list'|'data_room'} scope
   * @param {object} row — statement-status row from /api/user/statement-statuses
   * @return {string}
   */
  deriveStatementKeyFromStatusRow(scope, row) {
    if (row?.statement_key) return row.statement_key;
    if (scope === 'data_room') {
      if (!row?.is_market_sounding) return 'classic_dataroom';
      return row?.has_insider_informations?.length > 0 ? 'dataroom_with_insider_info' : 'dataroom_without_insider_info';
    }
    if (scope === 'insider_list') {
      const type = row?.type;
      if (type === 'permanent' || type === 'PERMANENT') return 'permanent_insider_info';
      if (row?.name === '-') return 'permanent_insider_info';
      return 'case_specific_insider_info';
    }
    return 'classic_dataroom';
  },
  /**
   * @param {string} html
   * @return {string}
   */
  htmlToPlainTextForStatement(html) {
    if (html == null || html === '') return '';
    const div = typeof document !== 'undefined' ? document.createElement('div') : null;
    if (!div)
      return String(html)
        .replace(/<[^>]+>/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();
    div.innerHTML = html;
    return (div.textContent || '')
      .replace(/\u00a0/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  },
  getTimezoneOffset() {
    return Math.ceil(new Date().getTimezoneOffset() / -60);
  },
  /**
   * @param {string} dateString
   * @return {boolean}
   */
  isISOString(dateString) {
    if (typeof dateString !== 'string') return false;

    const isoRegex = /\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}.\d{3}Z/;
    return isoRegex.test(dateString) && !isNaN(Date.parse(dateString));
  },
  /**
   * @param {Date | string} date
   * @return {Date}
   */
  applyTimezoneOffset(date) {
    if (typeof date !== 'string') date = date.toISOString();

    const regex = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2})\.(\d{6})Z$/;
    const match = date.match(regex);

    if (match) {
      const [_, year, month, day, hour, minute, second, microsecond] = match;
      const newDate = new Date(Date.UTC(year, month - 1, day, hour, minute, second, microsecond));
      return new Date(newDate.getTime() + this.getTimezoneOffset() * 60 * 60 * 1000);
    }

    return new Date(new Date(date).getTime() + this.getTimezoneOffset() * 60 * 60 * 1000);
  },
  /**
   * @param {string} date
   * @param {boolean} forceShowSeconds
   * @return {string}
   */
  applyTimezoneOffsetAndFormat(date, forceShowSeconds = false) {
    if (!date) return '';
    if (this.isISOString(date)) return this.dateFormat(date, forceShowSeconds);
    return this.dateFormat(this.applyTimezoneOffset(date), forceShowSeconds);
  },
  /**
   * @param {string} filename
   * @return {string}
   */
  removeDuplicatedExtension(filename) {
    const ext = filename.split('.').pop();
    if (filename.indexOf(ext) === filename.lastIndexOf(ext)) return filename;
    return filename.split('.').slice(0, -1).join('.');
  },
  /**
   * Wait for <img> descendants so html2canvas captures pixels (avoids empty icons in client-side PDF).
   * @param {HTMLElement} root
   */
  waitForImages(root) {
    const imgs = [...root.querySelectorAll('img')];
    return Promise.all(
      imgs.map(
        (img) =>
          new Promise((resolve) => {
            if (img.complete) {
              resolve();
              return;
            }
            img.addEventListener('load', () => resolve(), { once: true });
            img.addEventListener('error', () => resolve(), { once: true });
          }),
      ),
    );
  },
  /**
   * @param {string | HTMLElement} content
   * @param {string} filename
   */
  async generatePdf(html, filename, oldMethod = false) {
    if (oldMethod) {
      if (html instanceof HTMLElement) {
        await this.waitForImages(html);
      }
      const options = {
        margin: 16,
        filename: `${filename.replace('.pdf', '')}.pdf`,
        html2canvas: { scale: 2, letterRendering: true, useCORS: true },
        jsPDF: { unit: 'pt', format: 'a4', orientation: 'portrait' },
        pagebreak: { mode: ['avoid-all', 'css', 'legacy'] },
      };
      const source = html instanceof HTMLElement ? html : String(html);
      return html2pdf().from(source).set(options).output('blob');
    }
    const res = await fetch(import.meta.env.VITE_AXIOS_BASE_URL + '/api/pdf/generate', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        html: html.outerHTML,
      }),
    });
    return res.blob();
  },
  /**
   * @param {string | HTMLElement} content
   * @param {string} filename
   */
  async generatePdfBinary(content, filename, oldMethod = false) {
    return await this.generatePdf(content, filename, oldMethod);
  },
  /**
   * @param {string | HTMLElement} content
   * @param {string} filename
   */
  generateAndDownloadPdf(content, filename, oldMethod = false) {
    this.generatePdf(content, filename, oldMethod).then((blob) => {
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      a.click();
    });
  },
  getFullNameDependingOnLocale(name, locale) {
    const parts = name.split(' ');
    const firstName = parts[0];
    const lastName = parts.slice(1).join(' ');
    if (locale === 'hu') return `${lastName} ${firstName}`;
    return `${firstName} ${lastName}`;
  },
  disableScrolling() {
    document.body.style.overflow = 'hidden';
    document.documentElement.style.overflow = 'hidden';
  },
  enableScrolling() {
    document.body.style.overflow = 'auto';
    document.documentElement.style.overflow = 'auto';
  },
  /**
   * @param {string[]} roles
   * */
  hasRoles(...roles) {
    const userStore = useUserStore();
    return roles.some((role) => userStore.userAccess.roles.includes(role));
  },
  hasSub(...subs) {
    const userStore = useUserStore();
    return subs.some((sub) => userStore.userAccess.subscriptions.includes(sub));
  },
  isSubscribed() {
    const userStore = useUserStore();
    return (
      userStore.userAccess.subscriptions.includes('regulated_market_package') ||
      userStore.userAccess.subscriptions.includes('sme_package')
    );
  },
  /**
   * @param {string[]} permissions
   * */
  hasPermissions(...permissions) {
    const userStore = useUserStore();
    return permissions.some((permission) => userStore.userAccess.permissions.includes(permission));
  },

  getDateString(dateStr = null, dots = false) {
    if (!dateStr) return null;
    const date = new Date(dateStr);
    const year = date.getFullYear();
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');
    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');
    const seconds = date.getSeconds().toString().padStart(2, '0');
    return dots
      ? `${year}.${month}.${day} ${hours}:${minutes}:${seconds}`
      : `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
  },
};
